import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Sparkles, ShieldCheck, HeartHandshake, Layers, TrendingUp } from 'lucide-react';
import Navbar from '@/components/smartpos/Navbar';
import Footer from '@/components/smartpos/Footer';
import FloatingCTA from '@/components/smartpos/FloatingCTA';
import JsonLd from '@/components/smartpos/JsonLd';
import RevealOnScroll from '@/components/smartpos/RevealOnScroll';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { useLanguage } from '@/lib/LanguageContext';

const jsonLdData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'Sobre SmartPOS',
  description: 'En SmartPOS desarrollamos soluciones tecnológicas que ayudan a los negocios a digitalizar, simplificar y optimizar su gestión.',
  mainEntity: {
    '@type': 'Organization',
    name: 'SmartPOS',
    url: 'https://www.smartpos.es',
    email: 'info@smartpos.es',
    telephone: '+34936302521',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Av. Lluís Companys, 23, Local A',
      addressLocality: 'Sant Joan Despí',
      addressRegion: 'Barcelona',
      postalCode: '08970',
      addressCountry: 'ES'
    }
  }
};

const meta = {
  ES: { title: 'Quiénes Somos | SmartPOS', desc: 'En SmartPOS desarrollamos soluciones tecnológicas que ayudan a los negocios a digitalizar, simplificar y optimizar su gestión.' },
  EN: { title: 'About Us | SmartPOS', desc: 'At SmartPOS we develop technology solutions that help businesses digitalise, simplify and optimise their management.' },
  CA: { title: 'Qui Som | SmartPOS', desc: 'A SmartPOS desenvolupem solucions tecnològiques que ajuden els negocis a digitalitzar, simplificar i optimitzar la seva gestió.' },
  FR: { title: 'Qui sommes-nous | SmartPOS', desc: 'Chez SmartPOS, nous développons des solutions technologiques qui aident les entreprises à digitaliser, simplifier et optimiser leur gestion.' },
};

// Icono y color fijos de cada valor; los textos salen del idioma activo.
const VALUE_STYLES = [
  { icon: Sparkles, color: 'blue', key: 'innovation' },
  { icon: ShieldCheck, color: 'violet', key: 'commitment' },
  { icon: HeartHandshake, color: 'green', key: 'closeness' },
  { icon: Layers, color: 'blue', key: 'adaptability' },
  { icon: TrendingUp, color: 'violet', key: 'evolution' }];


export default function About() {
  const { lang, t } = useLanguage();
  const m = meta[lang] || meta.ES;
  useDocumentMeta({ title: m.title, description: m.desc, canonicalPath: '/sobre-nosotros' });

  const values = VALUE_STYLES.map((v) => ({
    ...v,
    title: t(`about.values.${v.key}.title`),
    text: t(`about.values.${v.key}.text`),
  }));

  return (
    <div className="min-h-screen font-body bg-white">
      <JsonLd data={jsonLdData} />
      <Navbar />
      <main id="contenido-principal">
        {/* Hero — Quiénes Somos */}
        <section className="relative pt-40 pb-32 lg:pt-48 lg:pb-40 min-h-[calc(100vh-5rem)] flex items-center overflow-hidden bg-white">
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

          <div className="relative max-w-5xl mx-auto px-6 text-center">
            <RevealOnScroll direction="up">
              <span className="inline-flex items-center gap-2 text-xs font-black tracking-[0.2em] uppercase text-brand-blue mb-8 px-5 py-2 rounded-full bg-blue-50 border border-blue-100">
                <Sparkles size={13} className="text-brand-green" />
                {t('about.eyebrow')}
              </span>
            </RevealOnScroll>
            <RevealOnScroll direction="up" delay={100}>
              <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-7xl text-gray-900 leading-[1.05] tracking-tight mb-8">
                {t('about.title1')}
                <span className="block brand-gradient-text font-semibold pb-3">{t('about.title2')}</span>
              </h1>
            </RevealOnScroll>
            <RevealOnScroll direction="up" delay={250}>
              <p className="text-gray-600 text-lg lg:text-xl font-light max-w-2xl mx-auto leading-relaxed">
                {t('about.desc1')}
              </p>
            </RevealOnScroll>
            <RevealOnScroll direction="up" delay={400}>
              <p className="text-gray-500 text-base lg:text-lg font-light max-w-2xl mx-auto leading-relaxed mt-5">
                {t('about.desc2')}
              </p>
            </RevealOnScroll>
          </div>
        </section>

        {/* Misión + Visión */}
        <section className="py-32 lg:py-40 bg-gray-50 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Misión */}
              <RevealOnScroll direction="left">
                <div className="relative bg-white rounded-3xl p-10 lg:p-14 shadow-lg shadow-blue-500/5 border border-gray-100 h-full overflow-hidden">
                  <div className="absolute -top-12 -right-12 w-48 h-48 bg-brand-blue/5 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="w-16 h-16 bg-brand-blue rounded-2xl flex items-center justify-center mb-8">
                      <Target size={28} className="text-white" />
                    </div>
                    <h2 className="font-heading font-semibold text-2xl text-gray-900 tracking-tight mb-4">{t('about.mission.title')}</h2>
                    <p className="font-heading font-semibold text-xl lg:text-2xl text-gray-900 mb-6 pb-1 brand-gradient-text">
                      {t('about.mission.claim')}
                    </p>
                    <p className="text-gray-600 leading-relaxed font-light text-lg">
                      {t('about.mission.text')}
                    </p>
                  </div>
                </div>
              </RevealOnScroll>

              {/* Visión */}
              <RevealOnScroll direction="right" delay={150}>
                <div className="relative bg-white rounded-3xl p-10 lg:p-14 shadow-lg shadow-violet-500/5 border border-gray-100 h-full overflow-hidden">
                  <div className="absolute -top-12 -right-12 w-48 h-48 bg-brand-violet/5 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="w-16 h-16 bg-brand-violet rounded-2xl flex items-center justify-center mb-8">
                      <Eye size={28} className="text-white" />
                    </div>
                    <h2 className="font-heading font-semibold text-2xl text-gray-900 tracking-tight mb-4">{t('about.vision.title')}</h2>
                    <p className="font-heading font-semibold text-xl lg:text-2xl text-gray-900 mb-6 pb-1 brand-gradient-text">
                      {t('about.vision.claim')}
                    </p>
                    <p className="text-gray-600 leading-relaxed font-light text-lg">
                      {t('about.vision.text')}
                    </p>
                  </div>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="py-32 lg:py-40 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <RevealOnScroll direction="up">
              <div className="text-center mb-16 lg:mb-20">
                <h2 className="font-heading font-light text-3xl lg:text-4xl text-gray-900 tracking-tight mb-3">{t('about.values.title')}</h2>
                <p className="text-gray-500 font-light max-w-xl mx-auto">{t('about.values.subtitle')}</p>
              </div>
            </RevealOnScroll>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {values.map(({ icon: Icon, title, text, color }, i) => {
                const colorBg = color === 'blue' ? 'bg-brand-blue' : color === 'violet' ? 'bg-brand-violet' : 'bg-brand-green';
                return (
                  <RevealOnScroll key={title} direction="up" delay={i * 100}>
                    <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-gray-100 hover-lift h-full">
                      <div className={`w-14 h-14 ${colorBg} rounded-2xl flex items-center justify-center mb-6`}>
                        <Icon size={24} className="text-white" />
                      </div>
                      <h3 className="font-heading font-semibold text-xl text-gray-900 tracking-tight mb-3">{title}</h3>
                      <p className="text-gray-600 leading-relaxed font-light">{text}</p>
                    </div>
                  </RevealOnScroll>);

              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 lg:py-40 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <RevealOnScroll direction="up">
              <h2 className="font-heading font-light text-3xl lg:text-4xl text-gray-900 tracking-tight mb-4">{t('about.cta.title')}</h2>
            </RevealOnScroll>
            <RevealOnScroll direction="up" delay={100}>
              <p className="text-gray-500 font-light mb-8 max-w-xl mx-auto">{t('about.cta.text')}</p>
            </RevealOnScroll>
            <RevealOnScroll direction="up" delay={200}>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/demo" className="inline-flex items-center justify-center gap-2 bg-brand-blue text-white px-7 py-3.5 rounded-full font-bold text-sm hover:opacity-90 transition-all hover:scale-105 shadow-lg shadow-blue-500/30">
                  {t('about.cta.demo')} <ArrowRight size={16} />
                </Link>
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 text-gray-700 px-7 py-3.5 rounded-full font-bold text-sm border border-gray-200 hover:bg-gray-50 transition-all">
                  {t('about.cta.contact')}
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingCTA />
    </div>);

}